import React from 'react';
import classnames from 'classnames';
import { Heading, HeadingProps } from '../heading';
import { Icon } from '../icon';

import styles from './accordion.module.scss';

type AccordionItem = {
  headline: { text: string | React.ReactNode; tag: HeadingProps['tag'] };
  content: React.ReactNode;
  isOpen: boolean;
  disabled?: boolean;
  onClick: () => void;
};

export type AccordionProps = {
  items: AccordionItem[];
} & Pick<React.HTMLAttributes<HTMLDivElement>, 'className'>;

const baseClass = 'accordion';
const itemClass = `${baseClass}__item`;

export const Accordion: React.FC<AccordionProps> = ({ items, className }) => {
  const classes = classnames(styles.accordion, className);
  return (
    <div className={classes} data-testid="accordion-component">
      {items.map(({ headline, content, isOpen, onClick, disabled }, idx) => {
        const contentClasses = classnames(styles[`${itemClass}__content`], {
          [styles[`${itemClass}__content--open`]]: isOpen
        });

        return (
          <div key={`accordion-${isOpen}-${idx}`} className={styles[`${itemClass}`]}>
            <Heading tag={headline.tag} variant="unstyled" className={styles[`${itemClass}__heading`]}>
              <button
                onClick={onClick}
                className={styles[`${itemClass}__button`]}
                aria-expanded={isOpen}
                aria-controls={`${itemClass}__content__${idx}`}
                id={`${itemClass}__button__${idx}`}
                aria-disabled={disabled}
                disabled={disabled}
              >
                <span className={styles[`${itemClass}__text`]}>{headline.text}</span>
                <Icon
                  className={styles[`${itemClass}__icon`]}
                  name={isOpen ? 'chevron-up--medium' : 'chevron-down--medium'}
                  aria-hidden="true"
                />
              </button>
            </Heading>
            <div
              id={`${itemClass}__content__${idx}`}
              className={contentClasses}
              role="region"
              aria-labelledby={`${itemClass}__button__${idx}`}
              aria-hidden={!isOpen}
            >
              {content}
            </div>
          </div>
        );
      })}
    </div>
  );
};
